import * as React from 'react';
import { memo } from 'react';

import { CitationModule } from '@coursera-web/cds-labs';

import { StyleSheet, css } from '@coursera/coursera-ui';

import type { DiscoveryCollection } from 'bundles/browse/components/types/DiscoveryCollections';
import type { EnrichedCatalogResultQuery } from 'bundles/browse/types/catalog-results';
import CampaignHeroModuleRenderer, {
  useShouldShowCampaignHeroModule,
} from 'bundles/campaigns/module-renderers/CampaignHeroModuleRenderer';
import CampaignPromoBarModuleRenderer, {
  useShouldShowCampaignPromoBarModule,
} from 'bundles/campaigns/module-renderers/CampaignPromoBarModuleRenderer';
import CollectionHeader from 'bundles/collections-carousel/components/carousel/CollectionHeader';
import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import {
  mapCollectionsForCarousel,
  mapLOHPCollectionForCarousel,
} from 'bundles/collections-carousel/utils/mappingUtils';
import {
  getShouldShowCourseraPlusMerchandisingPlacements,
  getShouldShowUnregisteredOnboarding,
} from 'bundles/coursera-plus-common/utils/subscriptionsForwardDiscoveryExperiment';
import GrowthDiscoveryExperiments from 'bundles/epic/clients/GrowthDiscovery';
import LocalizationExperiments from 'bundles/epic/clients/localization';
import type {
  CarouselMetadataFieldsFragment,
  ImageWithMetadataFieldsFragment,
  LoggedOutHomePageContentFieldsFragment as PageContent,
  TestimonialsFieldsFragment,
} from 'bundles/front-page/__generated__/contentfulTypes';
import { CAROUSEL_METADATA_COLLECTION_SLUG } from 'bundles/front-page/common/constants';
import DomainPills from 'bundles/front-page/components/modules/DomainPills';
import EnterpriseCtaRebrand from 'bundles/front-page/components/modules/EnterpriseCtaRebrand';
import FrontPageSkinnyBanner from 'bundles/front-page/components/modules/FrontPageSkinnyBanner';
import HeroBanner from 'bundles/front-page/components/modules/HeroBanner';
import OnboardingModule from 'bundles/front-page/components/modules/OnboardingModule';
import PartnerLogosCMS from 'bundles/front-page/components/modules/PartnerLogos';
import PartnerLogosSlim from 'bundles/front-page/components/modules/PartnerLogosSlim';
import PromoAdRedesign from 'bundles/front-page/components/modules/PromoAd';
import SecondaryConsumerCta from 'bundles/front-page/components/modules/SecondaryConsumerCta';
import SocialProofContainerCMS from 'bundles/front-page/components/modules/SocialProofContainer';
import CareerAcademyModuleRenderer from 'bundles/front-page/components/modules/career-academy/CareerAcademyModuleRenderer';
import Collections from 'bundles/front-page/components/modules/collections/Collections';
import CourseraUpsellHeroModuleRenderer from 'bundles/front-page/components/modules/hero-banner/CourseraUpsellHeroModuleRenderer';
import HeroBannerWithSearchBar from 'bundles/front-page/components/modules/hero-banner/HeroBannerWithSearchBar';
import HeroModuleRenderer from 'bundles/front-page/components/modules/hero-banner/HeroModuleRenderer';
import MerchandisingHeroModuleRenderer from 'bundles/front-page/components/modules/hero-banner/MerchandisingHeroModuleRenderer';
import { BG_C_SVG, getFallbackSocialProofs, getPartnerLogos } from 'bundles/front-page/constants/defaults';
import { getMostPopularCertsMetaData } from 'bundles/front-page/constants/mostPopularCerts';
import { FrontpageVariants, getModuleOptions, getModuleType } from 'bundles/front-page/constants/variants';
import { getCollectionKey } from 'bundles/front-page/hoc/WithCollectionsData';
import type { PropsFromWithMediaMaxWidthScreenTablet } from 'bundles/front-page/hoc/WithMediaMaxWidthTablet';
import type { Module, ModuleOptions, ModuleType } from 'bundles/front-page/types';
import {
  getAIDubbingCampaignContent,
  getAIDubbingCampaignExperiment,
} from 'bundles/front-page/utils/AIDubbingCampaign';
import { getCollectionContents } from 'bundles/front-page/utils/FrontPageDataUtils';
import { getRegionalContent, getRegionalExperiment } from 'bundles/front-page/utils/regionalPromotions';
import { getPathwayDisclaimer } from 'bundles/unified-common/utils/getPathwaysDisclaimers';

const styles = StyleSheet.create({
  backgroundContainer: {
    backgroundImage: `url(${BG_C_SVG})`,
    backgroundPosition: '100% 58%',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '70% auto',

    '@media screen and (max-width: 768px)': {
      backgroundPosition: '-25% 65%',
      backgroundSize: '150% auto',
    },

    '@media screen and (max-width: 480px)': {
      backgroundPosition: '100% 62.3%',
      backgroundSize: '260% auto',
    },
  },
});

export type Props = {
  pageContent: PageContent;
  premiumProductCollections?: EnrichedCatalogResultQuery[];
  discoveryCollections?: DiscoveryCollection[];
  collectionsLoading?: boolean;
  isUsRegion: boolean;
  countryCode?: string;
  setHasScrolledPastHero?: (hasScrolledPastHero: boolean) => void;
  setIsMobileSearchModalOpen?: (isMobileSearchModalOpen: boolean) => void;
} & PropsFromWithMediaMaxWidthScreenTablet;

export const renderModules = (
  props: Props,
  modules: readonly Module[],
  renderers: { [key: string]: ModuleRenderer }
) => {
  return modules.map((module: Module) => {
    const type = getModuleType(module);
    const opts = { ...getModuleOptions(module) };

    return <React.Fragment key={type}>{renderers[type](props, opts)}</React.Fragment>;
  });
};

export const getCollectionsForCarousel = (
  premiumProductCollections: EnrichedCatalogResultQuery[],
  discoveryCollections: DiscoveryCollection[],
  carouselMetadata: { [key: string]: CarouselMetadataFieldsFragment | null | undefined }
) => {
  const premiumProductCollectionsForCarousel = premiumProductCollections.map(
    (collection: EnrichedCatalogResultQuery, index) => {
      const key: string | null = getCollectionKey(collection);
      const metadata = carouselMetadata[key ?? ''];

      return {
        ...mapLOHPCollectionForCarousel(collection, index, metadata),
        header: (
          <CollectionHeader
            sectionTitle={metadata?.sectionTitle}
            title={metadata?.title}
            subtitle={metadata?.subtitle}
            url={metadata?.callToActionLink}
            trackingKey={key}
          />
        ),
        expandedExternalLinkButtonText: metadata?.callToActionLabel,
        expandedExternalLinkButtonUrl: metadata?.callToActionLink,
      };
    }
  ) as CarouselCollection[];

  const discoveryCollectionsForCarousel = mapCollectionsForCarousel(discoveryCollections);
  const discoveryCollectionsForCarouselWithMetadata = discoveryCollectionsForCarousel.map((collection) => {
    const key: string | null = getCollectionKey(collection);
    const metadata = carouselMetadata[key ?? ''];

    return {
      ...collection,
      header: (
        <CollectionHeader
          sectionTitle={metadata?.sectionTitle}
          title={metadata?.title}
          subtitle={metadata?.subtitle}
          url={metadata?.callToActionLink}
          trackingKey={key}
        />
      ),
      expandedExternalLinkButtonText: metadata?.callToActionLabel,
      expandedExternalLinkButtonUrl: metadata?.callToActionLink,
    };
  });

  return [...discoveryCollectionsForCarouselWithMetadata, ...premiumProductCollectionsForCarousel];
};

export type ModuleRenderer = (props: Props, opts: ModuleOptions) => React.ReactNode;

export const ModuleRenderers: { [key in ModuleType]: ModuleRenderer } = {
  HERO: ({
    pageContent: {
      hero: heroContent,
      outcomesC4BCtaText: entepriseCtaText,
      outcomesC4BCtaLink: enterpriseCtaLink,
      regionalHeroModulesCollection,
    },
    setHasScrolledPastHero,
    setIsMobileSearchModalOpen,
  }) => {
    const shouldShowCPlusMerchandisingPlacements = getShouldShowCourseraPlusMerchandisingPlacements();
    const shouldShowCampaignHeroModule = useShouldShowCampaignHeroModule();

    // Search Banner Test
    const searchBannerTest = GrowthDiscoveryExperiments.get('showSearchBannerOnLOHP');
    if (searchBannerTest) {
      return (
        <HeroBannerWithSearchBar
          content={heroContent}
          setHasScrolledPastHero={setHasScrolledPastHero}
          setIsMobileSearchModalOpen={setIsMobileSearchModalOpen}
        />
      );
    }

    // AIDubbingCampaign
    const aiDubbingCampaignExperiment = getAIDubbingCampaignExperiment();
    if (aiDubbingCampaignExperiment) {
      return (
        <MerchandisingHeroModuleRenderer
          content={getAIDubbingCampaignContent(regionalHeroModulesCollection?.items, aiDubbingCampaignExperiment)}
        />
      );
    }

    // Elevate LTV project
    if (shouldShowCPlusMerchandisingPlacements) {
      return <CourseraUpsellHeroModuleRenderer />;
    }

    // Self-serve campaign
    if (shouldShowCampaignHeroModule) {
      return <CampaignHeroModuleRenderer />;
    }

    // Regional promotions
    const regionalExperiment = getRegionalExperiment();
    if (regionalExperiment) {
      return (
        <MerchandisingHeroModuleRenderer
          content={getRegionalContent(regionalHeroModulesCollection?.items, regionalExperiment)}
        />
      );
    }

    // Default hero banner
    return (
      <HeroBanner entepriseCtaText={entepriseCtaText} enterpriseCtaLink={enterpriseCtaLink} content={heroContent} />
    );
  },
  WIDER_HERO_REDESIGN: ({ pageContent }) => {
    const {
      hero: heroContent,
      heroModuleBackgroundColor,
      outcomesC4BCtaText: secondaryButtonLabel,
      outcomesC4BCtaLink: secondaryButtonLink,
    } = pageContent;

    return (
      <HeroModuleRenderer
        content={heroContent}
        secondaryButtonLabel={secondaryButtonLabel}
        secondaryButtonLink={secondaryButtonLink}
        backgroundColor={heroModuleBackgroundColor}
      />
    );
  },
  PARTNERS: ({ pageContent }, opts) => {
    const { partnerLogosTitle } = pageContent;
    const partnerLogos = getCollectionContents<ImageWithMetadataFieldsFragment>(pageContent, 'partnerLogosCollection');

    // fallback to hardcoded partner logos if partnerLogos are not available
    const partnerLogosToShow = !partnerLogos || partnerLogos.length === 0 ? getPartnerLogos() : partnerLogos;

    const searchBannerTest = GrowthDiscoveryExperiments.get('showSearchBannerOnLOHP');
    if (searchBannerTest) {
      return <PartnerLogosSlim partnerLogos={partnerLogosToShow} />;
    }
    return <PartnerLogosCMS {...opts} partnerLogos={partnerLogosToShow} partnerLogosTitle={partnerLogosTitle} />;
  },
  ONBOARDING: () => {
    if (!getShouldShowUnregisteredOnboarding()) {
      return null;
    }
    return <OnboardingModule />;
  },
  CAREER_ROLES: () => {
    return <CareerAcademyModuleRenderer />;
  },
  LEARNER_OUTCOMES: ({ pageContent }, { hideEnterpriseCTA }) => {
    const { outcomes: outcomesContent, outcomesFootnote, outcomesC4BCtaText } = pageContent;
    return (
      <PromoAdRedesign
        content={{
          ...outcomesContent,
          outcomesFootnote,
          outcomesC4BCtaText: hideEnterpriseCTA ? null : outcomesC4BCtaText,
        }}
      />
    );
  },
  TESTIMONIALS: ({ pageContent }, opts) => {
    const testimonialsTitle = pageContent?.testimonialsTitle;
    const testimonials = getCollectionContents<TestimonialsFieldsFragment>(pageContent, 'testimonialsCollection');

    const testimonialsToShow = !testimonials || testimonials.length === 0 ? getFallbackSocialProofs() : testimonials;

    return <SocialProofContainerCMS {...opts} testimonials={testimonialsToShow} title={testimonialsTitle} />;
  },
  CONSUMER_CTA: (_, { modules, ...opts }) => <SecondaryConsumerCta {...opts} />,
  C4B_CTA: ({ pageContent, isUsRegion }, opts) => {
    const { c4BText, c4BTitle, c4BEnterpriseCtaText } = pageContent;

    const c4bPartnerLogos = getCollectionContents<ImageWithMetadataFieldsFragment>(
      pageContent,
      'c4BPartnersLogosCollection'
    );

    return (
      <EnterpriseCtaRebrand
        {...opts}
        isUsRegion={isUsRegion}
        partnerLogos={c4bPartnerLogos}
        c4BText={c4BText}
        c4BTitle={c4BTitle}
        c4BEnterpriseCtaText={c4BEnterpriseCtaText}
      />
    );
  },
  COLLECTIONS: ({ premiumProductCollections, discoveryCollections, pageContent, collectionsLoading }) => {
    const carouselMetadata = {
      CERTIFICATES: pageContent.certificateCarouselMetadata,
      degrees: pageContent.degreeCarouselMetadata,
      // "New on Coursera" collection
      iTQ3taQkSO2EYYPJrRZLpQ: pageContent.s18NCarouselMetadata,
      // "Start learning with free courses" collection
      '7WmAAysHTNu6qNB': pageContent.coursesCarouselMetadata,
      MASTERTRACKS: pageContent.mastertrackCarouselMetadata,
      UNIVERSITY_CERTIFICATES: pageContent.universityCertificatesCarouselMetadata,
      // eslint-disable-next-line camelcase
      stackable_degrees: pageContent.carouselsMetadataCollection?.items.find(
        (meta) => meta?.slug === CAROUSEL_METADATA_COLLECTION_SLUG.STACKABLE_DEGREES
      ),
      STACKABLE_SPECIALIZATIONS: pageContent.carouselsMetadataCollection?.items.find(
        (meta) => meta?.slug === CAROUSEL_METADATA_COLLECTION_SLUG.STACKABLE_SPECIALIZATIONS
      ),
      // "Most Popular Certificates" collection
      mostPaidEnrollmentsNumericTag: getMostPopularCertsMetaData(),
    };

    if (premiumProductCollections && discoveryCollections) {
      const carouselCollections = getCollectionsForCarousel(
        premiumProductCollections,
        discoveryCollections,
        carouselMetadata
      );
      return <Collections carouselCollections={carouselCollections} collectionsLoading={collectionsLoading} />;
    }
  },
  DOMAINS: () => <DomainPills />,
  BACKGROUND_WRAPPER: (props, { modules }) => {
    if (!modules) {
      return null;
    }

    return <div {...css(styles.backgroundContainer)}>{renderModules(props, modules, ModuleRenderers)}</div>;
  },
  SKINNY_BANNER: ({ pageContent: { promoBannerId, promoBannerText } }) => {
    if (useShouldShowCampaignPromoBarModule()) {
      return <CampaignPromoBarModuleRenderer />;
    }

    return <FrontPageSkinnyBanner promoBannerId={promoBannerId} promoBannerText={promoBannerText} />;
  },
};

export const FrontPageCmsWrapper = (props: Props) => {
  let localizationVariant = LocalizationExperiments.get('loggedOutHomePageContent:global-global');
  localizationVariant = props.countryCode?.toLowerCase() === 'in' ? 'B' : localizationVariant;

  const modules = FrontpageVariants[localizationVariant];

  return (
    <main className="rc-FrontPageWrapper cds" data-catchpoint="front-page-application" data-e2e="cms-lohp-content">
      {renderModules(props, modules, ModuleRenderers)}
      <CitationModule citations={getPathwayDisclaimer()} removeBottomPadding removeTopPadding />
    </main>
  );
};

FrontPageCmsWrapper.displayName = 'FrontPageCmsWrapper';

export default memo(FrontPageCmsWrapper);
